import React, { CSSProperties, useEffect, useState } from 'react'
import styled, { css } from 'styled-components'
import Attribute, { Value as BaseValue } from '../Attribute'
import { ColorFormat, Gradient } from '../../../../types'
import { gradientBackground, gradientAngle } from './math'
import checkerboardBackground from '@sketch/icons/inspector/checkerboard.svg'
import Opacity from '../Attribute/components/Opacity'
import { default as BaseRadialIcon } from './components/RadialGradientIcon'
import { default as BaseAngularIcon } from './components/AngularGradientIcon'
import { roundWithLocale } from '@sketch/utils'
import GradientPopover from './components/GradientPopover'
import { Tooltip, Flex } from '@sketch/components'
import copy from './copy'
import { ClickableArea } from '../Color/PlainColor'
import DirtyIconTooltip from '../Style/DirtyIconTooltip'
import { useHandleCopyValue } from '@sketch/modules-common'

const iconStyles = css`
  position: absolute;
  width: 17px;
  height: 17px;
  border-radius: 999px;
`

const Value = styled(BaseValue)`
  overflow: visible;
`

const RadialIcon = styled(BaseRadialIcon)`
  ${iconStyles};
`

const AngularIcon = styled(BaseAngularIcon)`
  ${iconStyles};
`

interface GradientProps {
  gradient: Gradient
  style?: CSSProperties
}

const GradientIcon = styled(({ gradient, style, ...props }: GradientProps) => (
  <div
    style={{ ...style, background: gradientBackground(gradient) }}
    {...props}
  />
))<{ gradient: Gradient }>`
  ${iconStyles};
`
const Border = styled.div`
  ${iconStyles};
  border: 1px solid ${({ theme }) => theme.colors.border.A};
`
const Background = styled.div`
  ${iconStyles};
  background-image: url(${checkerboardBackground});
  background-position: center center;
  background-size: cover;
  top: 0;
  left: 0;
`

interface GradientPreviewProps {
  className?: string
  gradient: Gradient
}

export const GradientPreview = styled(
  ({ className, gradient }: GradientPreviewProps) => {
    const icon =
      gradient.type === 'radial' ? (
        <RadialIcon gradient={gradient} />
      ) : gradient.type === 'angular' ? (
        <AngularIcon gradient={gradient} />
      ) : (
        <GradientIcon gradient={gradient} data-testid="gradient-preview" />
      )

    // A border does not cover the full gradient when we apply the border and
    // gradient on the same element. This leads to the opaque border rendering
    // incorrect colors.
    // We work around this by manually drawing the borders on top of the
    // background and gradient.
    return (
      <div className={className}>
        <Background />
        {icon}
        <Border />
      </div>
    )
  }
)`
  ${iconStyles};
  position: relative;
  margin-right: 4px;
  display: inline-block;
`

const Container = styled.div`
  display: flex;
  height: 24px;
  align-items: center;
  cursor: pointer;
  /* stylelint-disable-next-line scales/space */
  margin: -1px 0; /* Needed to adjust gradient height to 19px as any other attribute */
`

const GradientContainer = styled.div`
  display: flex;
  align-items: center;
  border: 1px solid ${({ theme }) => theme.colors.border.A};
  border-radius: 999px;
  padding: 1px 4px 1px 1px; /* stylelint-disable-line scales/space */
  line-height: normal; /* stylelint-disable-line scales/line-heights */
`

const GradientType = styled.span`
  text-transform: capitalize;
`

interface ColorFormatModifier {
  gradient: Gradient
  onColorFormatChange: (f: ColorFormat) => void
  colorFormat: ColorFormat
  opacity?: number
  dirtyAttributes?: { originalValue: Gradient; originalProperty: string }
}

function getOriginalValue(originalGradient: Gradient): string {
  const originalValues = []

  if (originalGradient.type) {
    originalValues.push(originalGradient.type)
  }

  if (
    originalGradient.from !== undefined &&
    originalGradient.to !== undefined
  ) {
    originalValues.push(
      `${roundWithLocale(gradientAngle(originalGradient), 0)}°`
    )
  }

  if (originalGradient.appearance?.opacity) {
    originalValues.push(originalGradient.appearance?.opacity)
  }

  if (originalGradient.stops?.length > 0) {
    originalValues.length > 0
      ? originalValues.push('and has different color stops')
      : originalValues.push('has different color stops')
  }

  return originalValues.join(', ')
}

const GradientAttribute: React.FCC<ColorFormatModifier> = ({
  gradient,
  onColorFormatChange,
  colorFormat,
  opacity = 1,
  dirtyAttributes,
  ...props
}) => {
  const [isPopoverOpen, setPopoverOpen] = useState(false)

  // Every time the gradient changes the popover should hide
  useEffect(() => {
    setPopoverOpen(false)
  }, [gradient])

  const gradientCopyValue = copy(gradient, opacity, colorFormat)

  const {
    handleCopyValue,
    copyTooltipText,
    handleEnterCopiableArea,
    handleLeaveCopiableArea,
    copyTooltipVisible,
  } = useHandleCopyValue(gradientCopyValue)

  return (
    <Attribute {...props}>
      <Tooltip
        style={{ width: '33%' }}
        placement="left"
        spacing="10px"
        visible={copyTooltipVisible}
        content={copyTooltipText}
      >
        <Flex>
          <ClickableArea
            aria-hidden
            onClick={handleCopyValue}
            onMouseEnter={handleEnterCopiableArea}
            onMouseLeave={handleLeaveCopiableArea}
          >
            Gradient
          </ClickableArea>
          {dirtyAttributes && (
            <Flex marginLeft={17}>
              <DirtyIconTooltip
                originalProperty={dirtyAttributes.originalProperty}
                originalValue={getOriginalValue(dirtyAttributes.originalValue)}
                preventLabelTooltip={handleLeaveCopiableArea}
              />
            </Flex>
          )}
        </Flex>
      </Tooltip>
      <Value label="Gradient" valueString={gradientCopyValue}>
        <GradientPopover
          visible={isPopoverOpen}
          gradient={gradient}
          onColorFormatChange={onColorFormatChange}
          colorFormat={colorFormat}
          onClickOutside={() => setPopoverOpen(false)}
          copyValue={gradientCopyValue}
          toggle={
            <Container>
              <GradientContainer onClick={() => setPopoverOpen(!isPopoverOpen)}>
                <GradientPreview gradient={gradient} />
                <span>
                  <GradientType>{gradient.type}</GradientType> (
                  {roundWithLocale(gradientAngle(gradient), 0)}˚)
                </span>
              </GradientContainer>
              <Opacity value={opacity} />
            </Container>
          }
        />
      </Value>
    </Attribute>
  )
}

export default GradientAttribute
